/**
 * Agent List Component
 * Displays all AI agents with filtering and CRUD operations
 */

import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  SmartToy as AgentIcon,
  Build as ToolIcon,
  FileUpload as ImportIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { agentApi, llmModelApi } from '../../services/api';
import type { AgentListItem, AgentCreate, AgentType, AgentUpdate, LlmModel } from '../../types';
import { PageHeader, LoadingSpinner, ConfirmDialog } from '../common';
import { LlmModelAutocomplete } from '../common/AsyncEntitySelect';
import { AdminAgentToolbar } from './AdminAgentToolbar';
import { AgentSyncButton } from './AgentSyncButton';
import { useAuth } from '../../context/AuthContext';
import { PromptEditor } from '../common/PromptEditor';

const AGENT_TYPE_OPTIONS: Array<{ value: AgentType; label: string; staffOnly?: boolean }> = [
  { value: 'ASSISTANT', label: 'Agent' },
  { value: 'AI_SYSTEM_BUILDER', label: 'AI System Builder' },
  { value: 'MAIN_ASSISTANT', label: 'Main Agent' },
  { value: 'KNOWLEDGE_GRAPH', label: 'Knowledge Graph (admin)', staffOnly: true },
  { value: 'BOT_BUILDER', label: 'Bot Builder' },
  { value: 'MODEL_LAB_BUILDER', label: 'Model Lab Builder (admin)', staffOnly: true },
];

const getAgentTypeLabel = (agentType?: AgentType) =>
  AGENT_TYPE_OPTIONS.find((option) => option.value === agentType)?.label.replace(' (admin)', '') || 'Agent';

const AGENTS_PAGE_SIZE = 20;

export const AgentList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [agentTypeFilter, setAgentTypeFilter] = useState<AgentType | ''>('');
  const [modelFilter, setModelFilter] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentListItem | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<AgentListItem | null>(null);
  const trimmedSearchTerm = searchTerm.trim();
  const hasActiveFilters = Boolean(trimmedSearchTerm || agentTypeFilter || modelFilter);

  // Fetch agents
  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['agents', page, AGENTS_PAGE_SIZE, trimmedSearchTerm, agentTypeFilter, modelFilter],
    queryFn: () =>
      agentApi.list(page, AGENTS_PAGE_SIZE, {
        search: trimmedSearchTerm || undefined,
        agent_type: agentTypeFilter || undefined,
        model: modelFilter || undefined,
      }),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Fetch LLM models for form
  const { data: modelsData } = useQuery({
    queryKey: ['models'],
    queryFn: () => llmModelApi.list({ page_size: 100 }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => agentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast.success('Agent deleted successfully');
      setDeletingAgent(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete agent:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
      setDeletingAgent(null);
    },
  });

  const handleView = (agentId: number) => {
    navigate(`/ai/agents/${agentId}`);
  };

  const handleChat = (agentId: number) => {
    navigate(`/ai/agents/${agentId}/chat`);
  };

  const handleEdit = (agent: AgentListItem) => {
    setEditingAgent(agent);
    setFormOpen(true);
  };

  const handleDelete = (agent: AgentListItem) => {
    setDeletingAgent(agent);
  };

  const handleCreateNew = () => {
    setEditingAgent(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingAgent(null);
  };

  // ----- Import agent (from a previously exported JSON file) -----
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPayload, setImportPayload] = useState<Record<string, unknown> | null>(null);
  const [importFileName, setImportFileName] = useState('');
  const [replaceExistingOnImport, setReplaceExistingOnImport] = useState(false);

  const importMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return agentApi.importAgent(payload, {
        replaceExisting: replaceExistingOnImport,
      });
    },
    onSuccess: (imported) => {
      toast.success(`Imported agent "${imported.name}"`);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      setImportDialogOpen(false);
      setImportPayload(null);
      setImportFileName('');
      setReplaceExistingOnImport(false);
      if (imported?.id) {
        navigate(`/ai/agents/${imported.id}`);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message || 'Failed to import agent');
    },
  });

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const handleImportDialogClose = () => {
    if (importMutation.isPending) return;
    setImportDialogOpen(false);
    setImportPayload(null);
    setImportFileName('');
    setReplaceExistingOnImport(false);
  };

  const handleChooseImportFile = () => {
    importInputRef.current?.click();
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      if (!payload || payload.export_type !== 'agent') {
        toast.error('File is not a valid agent export (export_type != "agent").');
        setImportPayload(null);
        setImportFileName('');
        return;
      }
      setImportPayload(payload);
      setImportFileName(file.name);
    } catch (err: any) {
      setImportPayload(null);
      setImportFileName('');
      toast.error(`Could not read file: ${err?.message || err}`);
    }
  };

  const handleImportSubmit = () => {
    if (!importPayload) {
      toast.error('Choose an agent export file first.');
      return;
    }
    importMutation.mutate(importPayload);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAgentTypeFilter('');
    setModelFilter('');
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const filteredAgents = agentsData?.results || [];
  const totalAgents = agentsData?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalAgents / AGENTS_PAGE_SIZE));
  const firstVisibleAgent = totalAgents === 0 ? 0 : (page - 1) * AGENTS_PAGE_SIZE + 1;
  const lastVisibleAgent = Math.min(page * AGENTS_PAGE_SIZE, totalAgents);

  // Count agents that need sync (for admin toolbar)
  const pendingUpdatesCount = filteredAgents.filter(
    agent => agent.needs_sync === true
  ).length;

  React.useEffect(() => {
    if (agentsData && totalAgents > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [agentsData, page, totalAgents, totalPages]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader
        title="Agents"
        actions={
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Create Agent</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={handleImportClick}
              disabled={importMutation.isPending}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Import</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Import</Box>
            </Button>
            {user?.is_staff && (
              <AdminAgentToolbar pendingUpdatesCount={pendingUpdatesCount} />
            )}
          </Box>
        }
      />

      {/* Search and filters */}
      <Box
        sx={{
          mb: { xs: 2, sm: 3 },
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          variant="outlined"
          size="small"
          sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 260 } }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 } }}>
          <InputLabel>Agent Type</InputLabel>
          <Select
            value={agentTypeFilter}
            label="Agent Type"
            onChange={(e) => {
              setAgentTypeFilter(e.target.value as AgentType | '');
              setPage(1);
            }}
          >
            <MenuItem value="">
              <em>All agent types</em>
            </MenuItem>
            {AGENT_TYPE_OPTIONS.filter((option) => !option.staffOnly || user?.is_staff).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
          <InputLabel>LLM Model</InputLabel>
          <Select
            value={modelFilter}
            label="LLM Model"
            onChange={(e) => {
              const value = e.target.value;
              setModelFilter(value === '' ? '' : Number(value));
              setPage(1);
            }}
          >
            <MenuItem value="">
              <em>All models</em>
            </MenuItem>
            {(modelsData?.results || []).map((model) => (
              <MenuItem key={model.id} value={model.id}>
                {model.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Button
            variant="text"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            size="small"
            sx={{ minHeight: 40 }}
          >
            Clear
          </Button>
        )}
      </Box>

      {totalAgents > 0 && (
        <Box
          sx={{
            mb: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {firstVisibleAgent}-{lastVisibleAgent} of {totalAgents} agents
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              siblingCount={1}
              boundaryCount={1}
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      )}

      {/* Agent List */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {filteredAgents.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                <AgentIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  No agents found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  {hasActiveFilters ? 'Adjust or clear the filters to see more agents' : 'Create your first agent to get started'}
                </Typography>
                {hasActiveFilters ? (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    size="small"
                  >
                    Clear Filters
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateNew}
                    size="small"
                  >
                    Create Agent
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredAgents.map((agent) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={agent.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="div"
                      sx={{ 
                        fontSize: { xs: '0.95rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        pr: 1,
                      }}
                    >
                      {agent.name}
                    </Typography>
                    <Chip
                      label={agent.is_active ? 'Active' : 'Inactive'}
                      color={agent.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </Box>

                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      display: '-webkit-box',
                      WebkitLineClamp: { xs: 2, sm: 3 },
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {agent.description || 'No description provided'}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={getAgentTypeLabel(agent.agent_type)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                    {agent.is_default && (
                      <Chip
                        label="Default"
                        color="success"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    )}
                    <Chip
                      icon={<ToolIcon />}
                      label={`${agent.tool_count} tools`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                    {agent.needs_sync && (
                      <Chip
                        label="Update"
                        color="warning"
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    )}
                    {agent.is_template && (
                      <Chip
                        label="Template"
                        color="warning"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    Model: {agent.model_name}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 }, pt: 0 }}>
                  <Box>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleChat(agent.id)}
                      >
                        <ChatIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(agent.id)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                  </Box>
                  <Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(agent)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="small"
            siblingCount={1}
            boundaryCount={1}
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <AgentFormDialog
        open={formOpen}
        agent={editingAgent}
        models={modelsData?.results || []}
        onClose={handleFormClose}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['agents'] });
          handleFormClose();
        }}
      />

      <Dialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={handleImportFileChange}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ImportIcon />}
                onClick={handleChooseImportFile}
                disabled={importMutation.isPending}
                size="small"
              >
                Choose file
              </Button>
              {importFileName && (
                <Chip
                  label={importFileName}
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={replaceExistingOnImport}
                  onChange={(event) => setReplaceExistingOnImport(event.target.checked)}
                  disabled={importMutation.isPending}
                />
              }
              label="Update matches"
              sx={{ alignSelf: 'flex-start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportDialogClose} disabled={importMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              importMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ImportIcon />
              )
            }
            onClick={handleImportSubmit}
            disabled={!importPayload || importMutation.isPending}
          >
            {importMutation.isPending ? 'Importing' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingAgent}
        title="Delete Agent"
        message={`Are you sure you want to delete "${deletingAgent?.name}"? This action cannot be undone.`}
        onConfirm={() => deletingAgent && deleteMutation.mutate(deletingAgent.id)}
        onCancel={() => setDeletingAgent(null)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

// Agent Form Dialog Component
interface AgentFormDialogProps {
  open: boolean;
  agent: AgentListItem | null;
  models: LlmModel[];
  onClose: () => void;
  onSuccess: () => void;
}

const AgentFormDialog: React.FC<AgentFormDialogProps> = ({
  open,
  agent,
  models,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<AgentCreate | AgentUpdate>({
    name: '',
    description: '',
    system_prompt: '',
    model_id: 0,
    stt_model_id: undefined,
    tts_model_id: undefined,
    agent_type: 'ASSISTANT',
    capabilities: [],
    is_active: true,
    is_default: false,
    is_template: false,
    template_source_id: undefined,
  });
  const [templates, setTemplates] = useState<AgentListItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Load templates on mount
  React.useEffect(() => {
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templateList = await agentApi.listTemplates();
        setTemplates(templateList);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, []);

  // Initialize form when agent changes
  React.useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name,
        description: agent.description,
        system_prompt: '', // Will be loaded from detail
        model_id: 0, // Will be loaded from detail
        stt_model_id: undefined,
        tts_model_id: undefined,
        agent_type: 'ASSISTANT',
        is_active: agent.is_active,
        is_default: Boolean(agent.is_default),
        template_source_id: undefined,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        system_prompt: 'You are a helpful AI agent.',
        model_id: models[0]?.id || 0,
        stt_model_id: undefined,
        tts_model_id: undefined,
        agent_type: 'ASSISTANT',
        capabilities: [],
        is_active: true,
        is_default: false,
        template_source_id: undefined,
      });
    }
  }, [agent, models]);

  // Handle template selection
  const handleTemplateChange = (templateId: number | '') => {
    if (templateId === '') {
      setFormData({ 
        ...formData, 
        template_source_id: undefined,
        agent_type: 'ASSISTANT',
      });
    } else {
      const selectedTemplate = templates.find(t => t.id === templateId);
      setFormData({ 
        ...formData, 
        template_source_id: templateId,
        agent_type: selectedTemplate?.agent_type || 'ASSISTANT',
      });
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: AgentCreate) => agentApi.create(data),
    onSuccess: () => {
      toast.success('Agent created successfully');
      queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentUpdate }) =>
      agentApi.update(id, data),
    onSuccess: () => {
      toast.success('Agent updated successfully');
      queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    },
  });

  const handleSubmit = () => {
    const payload = {
      ...formData,
      is_default: Boolean(formData.is_default) && Boolean(formData.is_active),
    };
    if (agent) {
      updateMutation.mutate({ id: agent.id, data: payload });
    } else {
      createMutation.mutate(payload as AgentCreate);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={window.innerWidth < 600}
    >
      <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {agent ? 'Edit Agent' : 'Create New Agent'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, mt: { xs: 1, sm: 2 } }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
            size="small"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
            size="small"
          />

          {/* Template Selection Dropdown - Only show when creating (not editing) */}
          {!agent && (
            <FormControl fullWidth size="small">
              <InputLabel>Provision from Template (Optional)</InputLabel>
              <Select
                value={formData.template_source_id || ''}
                label="Provision from Template (Optional)"
                onChange={(e) => handleTemplateChange(e.target.value as number | '')}
                disabled={loadingTemplates}
              >
                <MenuItem value="">
                  <em>None - Create from scratch</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({getAgentTypeLabel(template.agent_type)})
                    {template.owner_name ? (
                      <em style={{ marginLeft: 8, color: '#666' }}> — {template.owner_name}</em>
                    ) : null}
                  </MenuItem>
                ))}
              </Select>
              {formData.template_source_id && (
                <Typography variant="caption" color="info.main" sx={{ mt: 0.5 }}>
                  Agent will be provisioned from the selected template with pre-configured settings.
                </Typography>
              )}
            </FormControl>
          )}

          {/* Hide System Prompt when template is selected - it comes from template */}
          {!formData.template_source_id && (
            <Box>
              <PromptEditor
                label="System Prompt *"
                value={formData.system_prompt || ''}
                onChange={(value) =>
                  setFormData({ ...formData, system_prompt: value || '' })
                }
                helperText="Define the agent's role and behavior"
                minHeight={window.innerWidth < 600 ? 200 : 300}
                showInputs={false}
                showSnippets
                defaultShowSnippetPanel
              />
            </Box>
          )}

          {/* Hide Agent Type when template is selected */}
          {!formData.template_source_id && (
            <FormControl fullWidth required size="small">
              <InputLabel>Agent Type</InputLabel>
              <Select
                value={formData.agent_type}
                label="Agent Type"
                onChange={(e) => setFormData({ ...formData, agent_type: e.target.value as AgentType })}
              >
                {AGENT_TYPE_OPTIONS.filter(
                  (option) => !option.staffOnly || user?.is_staff || formData.agent_type === option.value
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Hide LLM Model when template is selected - it comes from template */}
          {!formData.template_source_id && (
            <LlmModelAutocomplete
              value={formData.model_id || null}
              onChange={(modelId) => setFormData({ ...formData, model_id: modelId || 0 })}
              label="LLM Model"
              required
              size="small"
              filters={{ is_active: true }}
            />
          )}

          {!formData.template_source_id && (
            <LlmModelAutocomplete
              value={formData.stt_model_id || null}
              onChange={(modelId) => setFormData({ ...formData, stt_model_id: modelId ?? null })}
              label="STT Model"
              helperText="Used for voice messages when the chat model cannot consume audio directly."
              size="small"
              filters={{ is_active: true, transcription_supported: true }}
            />
          )}

          {!formData.template_source_id && (
            <LlmModelAutocomplete
              value={formData.tts_model_id || null}
              onChange={(modelId) => setFormData({ ...formData, tts_model_id: modelId ?? null })}
              label="TTS Model"
              helperText="Used to synthesize assistant voice output."
              size="small"
              filters={{ is_active: true, speech_synthesis_supported: true }}
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formData.is_default) && Boolean(formData.is_active)}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                disabled={!formData.is_active}
              />
            }
            label={
              formData.is_template
                ? 'Default template for this agent type'
                : 'Default for this agent type'
            }
            sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
          />

          {/* Hide is_template toggle when template is selected */}
          {user?.is_staff && !formData.template_source_id && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_template || false}
                  onChange={(e) => setFormData({ ...formData, is_template: e.target.checked })}
                  color="warning"
                />
              }
              label="Template (Admin Only)"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createMutation.isPending || updateMutation.isPending}
          size="small"
        >
          {agent ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentList;
